import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Select from '../components/common/Select';
import Input from '../components/common/Input';
import Badge from '../components/common/Badge';
import ResponsiveTable from '../components/common/ResponsiveTable';
import InvoiceBrandingModal from '../components/billing/InvoiceBrandingModal';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { billingService, BillingInvoice, InvoiceStatus, triggerBlobDownload } from '../services/billingService';
import { useInvoices, useGenerateInvoice } from '../hooks/useBilling';

const STATUS_BADGE: Record<InvoiceStatus, { variant: 'secondary' | 'info' | 'success' | 'danger'; label: string }> = {
  draft: { variant: 'secondary', label: 'Draft' },
  sent: { variant: 'info', label: 'Sent' },
  paid: { variant: 'success', label: 'Paid' },
  cancelled: { variant: 'danger', label: 'Cancelled' },
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface FormState {
  clientId: string;
  warehouseId: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  gstPercent: string;
  labelQty: string;
  stickerQty: string;
  packingUnits: string;
  coldStoragePallets: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  clientId: '',
  warehouseId: '',
  billingPeriodStart: '',
  billingPeriodEnd: '',
  gstPercent: '18',
  labelQty: '',
  stickerQty: '',
  packingUnits: '',
  coldStoragePallets: '',
  notes: '',
};

// Local (not UTC) YYYY-MM-DD for the billing-period date pickers — used as the
// `max` so no future date can be chosen, and to compare against on submit.
function todayISO(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function fmtCurrency(val: string | number) {
  const n = parseFloat(String(val));
  return isNaN(n) ? '—' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d: string) {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day} ${months[parseInt(m, 10) - 1]} ${y}`;
}

export default function BillingPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canViewForm = hasPermission('billing:form-view');
  const canDownload = hasPermission('billing:download');
  const canManageBranding = hasPermission('billing:branding-manage');

  const [filterClientId, setFilterClientId] = useState<number | undefined>();
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<'clientId' | 'warehouseId' | 'billingPeriodStart' | 'billingPeriodEnd' | 'gstPercent', string>>>({});
  const [showVas, setShowVas] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  async function downloadPdf(invoice: BillingInvoice) {
    setDownloadingId(invoice.id);
    try {
      const blob = await billingService.downloadInvoicePdf(invoice.id);
      triggerBlobDownload(blob, `${invoice.invoiceNumber}.pdf`);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to download invoice';
      showToast(msg, 'error');
    } finally {
      setDownloadingId(null);
    }
  }

  // Clients for dropdowns
  const { data: clientsRaw } = useQuery({
    queryKey: ['clients', 'list', { filters: { limit: 100 } }],
    queryFn: () => api.get('/clients?limit=100&page=1') as any,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const clients: Array<{ id: number; companyName: string }> =
    (clientsRaw as any)?.data?.items ?? [];

  // Warehouses for generate form
  const { data: warehousesRaw } = useQuery({
    queryKey: ['warehouses', 'list'],
    queryFn: () => api.get('/warehouses?limit=100&page=1') as any,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const warehouses: Array<{
    id: number;
    name: string;
    companyName?: string | null;
    companyGstin?: string | null;
    companyPan?: string | null;
    bankName?: string | null;
    bankAccountNo?: string | null;
    bankIfsc?: string | null;
    bankBranch?: string | null;
  }> = (warehousesRaw as any)?.data?.items ?? [];

  // Warehouses this specific client actually holds stock in — scopes the
  // Generate Invoice form's Warehouse picker down from "every warehouse in
  // the tenant" to just the ones relevant to the selected client.
  const { data: clientWarehousesRaw, isFetching: isFetchingClientWarehouses } = useQuery({
    queryKey: ['inventory', 'client-warehouses', form.clientId],
    queryFn: () => api.get(`/inventory/client/${form.clientId}/warehouses`) as any,
    enabled: !!form.clientId,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const clientWarehouses: Array<{ id: number; name: string }> = useMemo(
    () => (clientWarehousesRaw as any)?.data ?? [],
    [clientWarehousesRaw],
  );

  // Falls back to every warehouse only when the client has no inventory
  // anywhere yet (e.g. a brand-new client) — otherwise scoped to its real stock.
  const formWarehouses = form.clientId && clientWarehouses.length > 0 ? clientWarehouses : warehouses;

  // Auto-select the only warehouse a client operates out of — nothing left
  // for the user to pick. With more than one, the field just narrows down to
  // that client's actual warehouses instead of the full tenant-wide list.
  useEffect(() => {
    if (!form.clientId || clientWarehouses.length !== 1) return;
    const onlyWarehouseId = String(clientWarehouses[0].id);
    setForm((prev) => (prev.warehouseId === onlyWarehouseId ? prev : { ...prev, warehouseId: onlyWarehouseId }));
  }, [form.clientId, clientWarehouses]);

  // Locked whenever the client resolves to exactly one warehouse (nothing to
  // choose) — also locked mid-fetch, right after switching clients, so the
  // field can't be used while it's still showing the previous/fallback list.
  const warehouseFieldDisabled = !!form.clientId && (isFetchingClientWarehouses || formWarehouses.length === 1);
  const warehousePlaceholder = isFetchingClientWarehouses ? 'Loading warehouses…' : 'Select warehouse...';

  const { data: invoices = [], isLoading } = useInvoices(filterClientId);
  const generateMutation = useGenerateInvoice();

  const clientOptions = [
    { value: '', label: 'All Clients' },
    ...clients.map((c) => ({ value: c.id, label: c.companyName })),
  ];

  const formWarehouseOptions = [
    { value: '', label: 'Select warehouse...' },
    ...formWarehouses.map((w) => ({ value: w.id, label: w.name })),
  ];

  const formClientOptions = [
    { value: '', label: 'Select client...' },
    ...clients.map((c) => ({ value: c.id, label: c.companyName })),
  ];

  const displayed = invoices.filter((inv) =>
    filterStatus ? inv.status === filterStatus : true,
  );

  function setField(key: keyof FormState, val: string) {
    setForm((prev) => ({ ...prev, [key]: val }));
    setFormErrors((prev) => (prev[key as keyof typeof prev] ? { ...prev, [key]: undefined } : prev));
  }

  // Validates top-to-bottom and stops at the first empty required field, so
  // only one message shows at a time — matching the form's field order.
  function validateForm(): boolean {
    if (!form.clientId) {
      setFormErrors({ clientId: 'Client is required' });
      return false;
    }
    if (!form.warehouseId) {
      setFormErrors({ warehouseId: 'Warehouse is required' });
      return false;
    }
    if (!form.billingPeriodStart) {
      setFormErrors({ billingPeriodStart: 'Billing Period Start is required' });
      return false;
    }
    if (!form.billingPeriodEnd) {
      setFormErrors({ billingPeriodEnd: 'Billing Period End is required' });
      return false;
    }
    const today = todayISO();
    if (form.billingPeriodStart > today) {
      setFormErrors({ billingPeriodStart: 'Start date cannot be in the future' });
      return false;
    }
    if (form.billingPeriodEnd > today) {
      setFormErrors({ billingPeriodEnd: 'End date cannot be in the future' });
      return false;
    }
    if (form.billingPeriodEnd < form.billingPeriodStart) {
      setFormErrors({ billingPeriodEnd: 'End date cannot be before start date' });
      return false;
    }
    if (form.gstPercent) {
      const gst = Number(form.gstPercent);
      if (isNaN(gst) || gst < 0 || gst > 99) {
        setFormErrors({ gstPercent: 'GST % must be between 0 and 99' });
        return false;
      }
    }
    setFormErrors({});
    return true;
  }

  // The invoice's legal/bank header is drawn from the warehouse's Company &
  // Billing Details. Block generation (and the backend re-checks) when they're
  // not filled in the Warehouse Management module.
  function missingWarehouseDetails(warehouseId: number): string[] {
    const w = warehouses.find((x) => x.id === warehouseId);
    if (!w) return [];
    const required: Array<[keyof typeof w, string]> = [
      ['companyName', 'Company Name'],
      ['companyGstin', 'GSTIN'],
      ['companyPan', 'PAN'],
      ['bankName', 'Bank Name'],
      ['bankAccountNo', 'Account Number'],
      ['bankIfsc', 'IFSC'],
      ['bankBranch', 'Branch'],
    ];
    return required
      .filter(([field]) => {
        const value = w[field];
        return typeof value !== 'string' || value.trim() === '';
      })
      .map(([, label]) => label);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    const missing = missingWarehouseDetails(Number(form.warehouseId));
    if (missing.length > 0) {
      showToast(
        `Warehouse details missing. Please fill the warehouse's Company & Billing Details (${missing.join(', ')}) in the Warehouse Management module before generating an invoice.`,
        'error',
      );
      return;
    }

    try {
      const payload: any = {
        clientId: Number(form.clientId),
        warehouseId: Number(form.warehouseId),
        billingPeriodStart: form.billingPeriodStart,
        billingPeriodEnd: form.billingPeriodEnd,
        gstPercent: form.gstPercent ? Number(form.gstPercent) : 0,
      };
      if (form.labelQty) payload.labelQty = Number(form.labelQty);
      if (form.stickerQty) payload.stickerQty = Number(form.stickerQty);
      if (form.packingUnits) payload.packingUnits = Number(form.packingUnits);
      if (form.coldStoragePallets) payload.coldStoragePallets = Number(form.coldStoragePallets);
      if (form.notes.trim()) payload.notes = form.notes.trim();

      await generateMutation.mutateAsync(payload);
      showToast('Invoice generated successfully', 'success');
      setShowModal(false);
      setForm(EMPTY_FORM);
      setFormErrors({});
      setShowVas(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to generate invoice';
      showToast(msg, 'error');
    }
  }

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      width: '15%',
      render: (_: any, row: BillingInvoice) => (
        <span className="font-mono text-sm font-semibold text-brand-700">{row.invoiceNumber}</span>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      width: '20%',
      render: (_: any, row: BillingInvoice) => (
        <span className="text-sm text-gray-800">{row.client?.companyName ?? `Client ${row.clientId}`}</span>
      ),
    },
    {
      key: 'billingPeriodStart',
      label: 'Billing Period',
      width: '20%',
      render: (_: any, row: BillingInvoice) => (
        <span className="text-sm text-gray-600">
          {fmtDate(row.billingPeriodStart)} – {fmtDate(row.billingPeriodEnd)}
        </span>
      ),
    },
    {
      key: 'subtotal',
      label: 'Subtotal',
      width: '12%',
      render: (_: any, row: BillingInvoice) => (
        <span className="text-sm text-gray-700">{fmtCurrency(row.subtotal)}</span>
      ),
    },
    {
      key: 'totalAmount',
      label: 'Total',
      width: '12%',
      render: (_: any, row: BillingInvoice) => (
        <span className="text-sm font-semibold text-gray-900">{fmtCurrency(row.totalAmount)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '10%',
      render: (_: any, row: BillingInvoice) => {
        const s = STATUS_BADGE[row.status];
        return <Badge variant={s.variant} size="sm" rounded>{s.label}</Badge>;
      },
    },
    {
      key: 'createdAt',
      label: 'Generated',
      width: '11%',
      render: (_: any, row: BillingInvoice) => (
        <span className="text-xs text-gray-500">{fmtDate(row.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '180px',
      render: (_: any, row: BillingInvoice) => (
        <div className="flex items-center gap-2">
          {canViewForm && (
            <button
              type="button"
              title="View Invoice"
              className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 flex items-center whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/billing/invoices/${row.id}`);
              }}
            >
              <i className="fas fa-eye mr-1" />
              View
            </button>
          )}
          {canDownload && (
            <button
              type="button"
              title="Download PDF"
              className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-60 flex items-center whitespace-nowrap"
              disabled={downloadingId === row.id}
              onClick={(e) => {
                e.stopPropagation();
                downloadPdf(row);
              }}
            >
              <i className={`fas ${downloadingId === row.id ? 'fa-spinner fa-spin' : 'fa-download'} mr-1`} />
              Download
            </button>
          )}
          {!canViewForm && !canDownload && <span className="text-xs text-gray-400">—</span>}
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 lg:mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-extrabold text-ink-900">Billing</h1>
            <p className="text-sm text-ink-500 mt-0.5">
              Generate and manage client invoices
              {displayed.length > 0 && (
                <span className="text-ink-400"> ({displayed.length} {displayed.length === 1 ? 'invoice' : 'invoices'})</span>
              )}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {canManageBranding && (
              <Button
                variant="outline"
                icon="fa-image"
                onClick={() => setShowBrandingModal(true)}
                className="w-full sm:w-auto"
              >
                Invoice Branding
              </Button>
            )}
            <Button
              variant="primary"
              icon="fa-plus"
              onClick={() => setShowModal(true)}
              className="w-full sm:w-auto"
            >
              Generate Invoice
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 lg:mb-6">
          <Select
            options={clientOptions}
            value={filterClientId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setFilterClientId(v ? Number(v) : undefined);
            }}
            placeholder="All Clients"
          />
          <Select
            options={STATUS_OPTIONS}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            placeholder="All Statuses"
          />
        </div>

        {/* Table / mobile cards */}
        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable
              columns={columns}
              data={displayed}
              isLoading={isLoading}
              height="calc(100vh - 280px)"
              emptyMessage="No invoices found"
              onRowClick={canViewForm ? (row: BillingInvoice) => navigate(`/billing/invoices/${row.id}`) : undefined}
            />
          </div>
        </div>

        {/* Generate Invoice Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setForm(EMPTY_FORM); setFormErrors({}); setShowVas(false); }}
          title="Generate Invoice"
          size="lg"
          footer={
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setFormErrors({}); setShowVas(false); }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                onClick={handleSubmit as any}
                disabled={generateMutation.isPending}
                className="w-full sm:w-auto"
              >
                {generateMutation.isPending ? 'Generating...' : 'Generate'}
              </Button>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Client *"
                options={formClientOptions}
                value={form.clientId}
                onChange={(e) => {
                  setField('clientId', e.target.value);
                  setField('warehouseId', '');
                }}
                error={formErrors.clientId}
              />
              <Select
                label="Warehouse *"
                options={formWarehouseOptions}
                value={form.warehouseId}
                onChange={(e) => setField('warehouseId', e.target.value)}
                error={formErrors.warehouseId}
                disabled={warehouseFieldDisabled}
                placeholder={warehousePlaceholder}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Billing Period Start *"
                type="date"
                value={form.billingPeriodStart}
                onChange={(e) => setField('billingPeriodStart', e.target.value)}
                error={formErrors.billingPeriodStart}
                max={todayISO()}
              />
              <Input
                label="Billing Period End *"
                type="date"
                value={form.billingPeriodEnd}
                onChange={(e) => setField('billingPeriodEnd', e.target.value)}
                error={formErrors.billingPeriodEnd}
                min={form.billingPeriodStart || undefined}
                max={todayISO()}
              />
            </div>

            <Input
              label="Combined GST % (split evenly into CGST + SGST)"
              type="number"
              value={form.gstPercent}
              onChange={(e) => setField('gstPercent', e.target.value)}
              placeholder="0"
              maxLength={2}
              error={formErrors.gstPercent}
            />

            {/* VAS toggle */}
            <div>
              <button
                type="button"
                className="text-sm text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"
                onClick={() => setShowVas(!showVas)}
              >
                <i className={`fas fa-chevron-${showVas ? 'up' : 'down'} text-xs`} />
                Value Added Services (optional)
              </button>

              {showVas && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <Input
                    label="Label Qty"
                    type="number"
                    value={form.labelQty}
                    onChange={(e) => setField('labelQty', e.target.value)}
                    placeholder="0"
                    maxLength={5}
                  />
                  <Input
                    label="Hamali labour Qty"
                    type="number"
                    value={form.stickerQty}
                    onChange={(e) => setField('stickerQty', e.target.value)}
                    placeholder="0"
                    maxLength={5}
                  />
                  <Input
                    label="Packing Units"
                    type="number"
                    value={form.packingUnits}
                    onChange={(e) => setField('packingUnits', e.target.value)}
                    placeholder="0"
                    maxLength={5}
                  />
                  <Input
                    label="Cold Storage Pallets"
                    type="number"
                    value={form.coldStoragePallets}
                    onChange={(e) => setField('coldStoragePallets', e.target.value)}
                    placeholder="0"
                    maxLength={5}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                rows={2}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Optional notes..."
              />
            </div>
          </form>
        </Modal>

        {/* Invoice Branding Modal — mounted only when open so it fetches lazily */}
        {canManageBranding && showBrandingModal && (
          <InvoiceBrandingModal
            isOpen={showBrandingModal}
            onClose={() => setShowBrandingModal(false)}
          />
        )}
      </div>
    </Layout>
  );
}
